import type { paths as chorePaths } from "@securityjourney/svc-report/dist/gen/chore-report.d.ts";

import { OpenApiFetchWrapper } from "../openApiFetchWrapper";
import type { ChoreFilter } from "./reportTypes";

type ChoreStatusQuery = NonNullable<
  chorePaths["/svc/report/{tenant}/chores/status"]["get"]["parameters"]["query"]
>;
// The generated query type models only `filter.startDate.start.*`; the service also
// accepts `filter.startDate.end.*` (end-of-range), so extend it with those keys.
type ChoreStatusQueryWithEnd = ChoreStatusQuery & {
  "filter.startDate.end.seconds"?: number;
  "filter.startDate.end.nanos"?: number;
};

let instance: ChoreReportClient | null = null;

export function getChoreReportClient(): ChoreReportClient {
  if (!instance) {
    instance = new ChoreReportClient();
  }
  return instance;
}

export class ChoreReportClient extends OpenApiFetchWrapper<chorePaths> {
  constructor() {
    super();
  }

  async getStatus(tenant: string, filter?: ChoreFilter, includeUserDetails?: boolean) {
    const queryParams = buildChoreQueryParams(filter, includeUserDetails);
    return await this.get("/svc/report/{tenant}/chores/status", {
      params: { path: { tenant }, query: queryParams },
    });
  }

  async downloadStatusCsv(
    tenant: string,
    filter?: ChoreFilter,
    includeUserDetails?: boolean
  ): Promise<void> {
    const queryParams = buildChoreQueryParams(filter, includeUserDetails);
    const res = await this.client.GET("/svc/report/{tenant}/chores/status", {
      headers: { Accept: "text/csv" },
      parseAs: "stream",
      params: { path: { tenant }, query: queryParams },
    });
    const filename = parseDispositionFilename(res.response) || "assignments-status.csv";
    const blob = await res.response.blob();
    downloadFile(blob, filename);
  }
}

function buildChoreQueryParams(
  filter?: ChoreFilter,
  includeUserDetails?: boolean
): ChoreStatusQueryWithEnd {
  const queryParams: ChoreStatusQueryWithEnd = {};
  if (filter?.startDate?.start) {
    queryParams["filter.startDate.start.seconds"] = Math.floor(
      new Date(filter.startDate.start).getTime() / 1000
    );
    queryParams["filter.startDate.start.nanos"] = 0;
  }
  if (filter?.startDate?.end) {
    queryParams["filter.startDate.end.seconds"] = Math.floor(
      new Date(filter.startDate.end).getTime() / 1000
    );
    queryParams["filter.startDate.end.nanos"] = 0;
  }
  if (includeUserDetails !== undefined) {
    queryParams.includeUserDetails = includeUserDetails;
  }
  return queryParams;
}

function downloadFile(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.style.display = "none";
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  a.remove();
}

function parseDispositionFilename(response: Response): string | null {
  const disposition = response.headers.get("Content-Disposition");
  if (!disposition) return null;
  const match = /filename="([^"]+)"/.exec(disposition);
  return match?.[1] ?? null;
}
